import React, { type JSX, useState } from 'react';

import Icon from 'components/DesignSystem/Icon';
import Switch from 'components/DesignSystem/Switch';
import Indicator from 'components/Admin/EnablementIndicator';
import ProductShortList from 'components/Admin/Products/ShortList';
import SendTokenButton from 'components/Admin/Users/UserRow/Actions/SendTokenButton';
import ResetPasswordButton from 'components/Admin/Users/UserRow/Actions/ResetPasswordButton';
import EditUserButton from 'components/Admin/Users/UserRow/Actions/EditUserButton';
import { confirmationDialog, useRequestUserActivityToggle } from 'components/Admin/Users/UserRow/Actions/utils';
import { ListedUser } from 'helper/users';
import getInitials from 'utils/getInitials';
import useToggle from 'utils/hooks/useToggle';

import Details from './Details';

import './index.scss';

export default function UserCard({ user, setConfirmationDialogBox }: {
  user: ListedUser,
  setConfirmationDialogBox: (open: boolean, body: JSX.Element, confirm: () => void) => void,
}) {
  const [expanded, setExpanded] = useState(false);
  const userName = `${user.first_name} ${user.last_name}`;
  const { requestUserActivityToggle } = useRequestUserActivityToggle({ user_id: user.id });
  const { toggled, callback: changeUserActivity } = useToggle(
    user.active,
    requestUserActivityToggle,
  );

  return (
    <div className="users-mobile-card">
      <div className="users-mobile-card-summary">
        <div className="users-mobile-card-avatar">{getInitials(userName)}</div>
        <div className="users-mobile-card-info">
          <p className="users-mobile-card-name">{userName}</p>
          <p className="users-mobile-card-line">
            <Icon iconType="user" size="small" />
            {user.username}
          </p>
          <p className="users-mobile-card-line">
            <Icon iconType="archives" size="small" />
            {user.companyName}
          </p>
        </div>
        <Indicator active={toggled} suffix="o" />
      </div>

      <div className="users-mobile-card-products">
        <ProductShortList products={user.products} />
      </div>

      <button
        type="button"
        className="users-mobile-card-expand"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? 'Ver menos' : 'Ver detalhes'}
        <Icon iconType={expanded ? 'up_arrow' : 'down_arrow'} size="small" />
      </button>

      {expanded && (
        <div className="users-mobile-card-details">
          <Details user={user} />
        </div>
      )}

      <div className="users-mobile-card-actions">
        <div className="users-mobile-card-toggle">
          <Switch
            className="users-mobile-switch"
            checked={toggled}
            aria-label={`Atividade de ${userName}`}
            onChange={() => {
              setConfirmationDialogBox(true, confirmationDialog(toggled), changeUserActivity);
            }}
          />
          <span className={toggled ? 'users-mobile-switch-on' : 'users-mobile-switch-off'}>
            {toggled ? 'Habilitado' : 'Desabilitado'}
          </span>
        </div>
        <div className="users-mobile-card-buttons">
          <SendTokenButton userId={user.id} />
          <ResetPasswordButton userId={user.id} username={user.username} />
          <EditUserButton userId={user.id} />
        </div>
      </div>
    </div>
  );
}
